/**
 * useAuth — session state for the dashboard: login/register/logout, the
 * login activity log. The Console has NO RBAC and exactly one view, so there is
 * no view-mode gating here: ADR 0004 / E06 retired the operator/research dual view
 * and it has been removed.
 *
 * Owns everything App.jsx previously held about auth. The login log prints
 * REAL events only (authenticate, token received, ready); the legacy fake
 * boot sequence is gone by design (see .impeccable.md: honest by default).
 *
 * Offline mode (VITE_OFFLINE_AUTH=true) bypasses the backend for login only.
 *
 * F14 adds the second half of the credential lifecycle: the /reset-password
 * landing (a route since ADR 0009, same as L01's /verify-email), the recovery
 * screen, and the two post-mutation sign-outs. Both password mutations now
 * END the session that made them (the backend increments
 * `users.credential_version`), so both finish on the login screen carrying a
 * `loginNotice` that says why.
 */

import { useState, useEffect, useCallback, useRef, useReducer, useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router";
import * as api from "../engine/api";
import { decodeToken, getSessionKey } from "../engine/auth";
import { ROUTES } from "../engine/routePaths";
import { beginSession, endSession } from "../engine/visitedScreens";
import { USE_API } from "../engine/env";
import useResendCooldown from "./useResendCooldown";
import usePolledFetch from "./usePolledFetch";

// Small pause so the final "Ready" log line is visible before the shell mounts.
const READY_DELAY_MS = 300;

// OMEGA-1024 FR11 — how often the Console asks whether the platform is accepting
// work. FR20's 90-second recovery bound is this number plus the backend's own 30s
// cache TTL, so changing it changes a published bound rather than a poll rate.
const PLATFORM_STATUS_POLL_MS = 60000;

// The three emailed landing paths, the `?token` readers and the `?action` validator that
// stood here are GONE (ADR 0009). They were a hand-rolled path matcher: `readTokenLanding`
// compared `window.location.pathname` to a constant and pulled the token off
// `window.location.search`, and the comment beside RESET_PATH read "adding a router for two
// landing paths would be a far larger change than the paths are worth". That premise was
// true at two paths. A13 added a third, noting in passing that it was "the third user of the
// same routerless mechanism", and E23.9 added the two /registration returns without
// revisiting it either. Five hand-matched paths is a router nobody designed.
//
// The paths themselves are unchanged and now live in engine/routePaths.js; App.jsx matches
// them as routes and reads `?token` once at mount, which is the same semantics the useState
// initializers here gave. What this hook keeps is the SESSION: who is signed in, with what
// kind of token, and what the login screen should say. Which screen is showing is the URL's
// job now.

// E23.6 — the only session kind every customer route admits. Anything else is a
// registration session, INCLUDING a token minted before S4 that carries no
// `session_kind` at all: the backend refuses that one too (E23.6 D3), so reading an
// absent claim as full here would enter a shell whose every panel answers 403.
const FULL_SESSION_KIND = "full";

/** True only for a token whose `session_kind` claim is `full`; fails closed. */
function isFullSessionToken(token) {
  return decodeToken(token)?.session_kind === FULL_SESSION_KIND;
}

// E23.9 FR2 — the stored token is read through useSyncExternalStore, so every render
// sees the current one. The browser fires `storage` in every OTHER same-origin tab when
// one writes localStorage (never in the writer), which is what re-renders this tab
// when a card is added and a full token stored elsewhere. A write in this tab needs no
// event: each of its writers (doLogin, register, verifyEmail) is followed by a state
// change that re-renders, and the card step's completion requests one explicitly
// (handleRegistrationComplete). Module-level so React never re-subscribes.
function subscribeToTokenWrites(onChange) {
  if (typeof window === "undefined" || !window.addEventListener) return () => {};
  window.addEventListener("storage", onChange);
  return () => window.removeEventListener("storage", onChange);
}

function readStoredToken() {
  return api.getToken() || "";
}

export default function useAuth() {
  // Navigation is the router's, not this hook's. Every callback below that used to flip
  // `authView` or call `history.replaceState` now navigates, which is what keeps the address
  // bar and the rendered screen from disagreeing — the two had drifted apart by design
  // before, and that is precisely what made `completeReset` subtle (see FR10 below).
  const navigate = useNavigate();
  const location = useLocation();

  const [loggedIn, setLoggedIn] = useState(() => USE_API && api.hasToken());
  const [user, setUser] = useState("");
  // OMEGA-814 — what the Console CALLS the customer, as distinct from what it signs them
  // in with. `user` is the login-form field and therefore holds the login identity (an
  // email, for any account created from migration 091 onward). Rendering that in the
  // header is a demotion: the customer gave us a name at registration and the UI should
  // use it. Kept as separate state rather than derived from `user` because the two answer
  // different questions and only one of them is typed at a keyboard.
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [loginLog, setLoginLog] = useState([]);
  const [loginError, setLoginError] = useState("");
  // L01 — a 403 email_not_verified is a recoverable state (resend the link), not
  // a wrong-credentials failure; the login screen renders it distinctly.
  const [needsVerification, setNeedsVerification] = useState(false);
  // OMEGA-563 — the login-screen resend had NO guard at all (spammable). Add an
  // in-flight lock plus the shared cooldown so a rapid re-click can't fire the
  // real outbound-email endpoint the backend would only silently drop.
  const [resendingVerification, setResendingVerification] = useState(false);
  const {
    remaining: resendCooldownRemaining,
    onCooldown: resendOnCooldown,
    start: startResendCooldown,
  } = useResendCooldown();
  // `authView` is gone with the landing state. It was a three-value string ("login" |
  // "register" | "forgot") selecting one of three screens, which is exactly what a URL is;
  // the three are now /login, /register and /forgot-password. The callbacks below kept
  // their names and their state hygiene, and navigate instead of calling a setter, so no
  // caller changed.

  // A one-shot neutral message shown ON the login screen, for the cases where
  // the thing worth telling the user happened on a screen we are about to leave:
  // a completed reset (FR10) and a completed authenticated change (FR11) both
  // end with the session gone, so their success copy cannot live where the
  // action was taken. Distinct from loginError — this is not a failure.
  const [loginNotice, setLoginNotice] = useState("");

  // E23.9 FR2 — whether the stored token is a registration session, DERIVED on every
  // render and never held in state. A cached standing is the defect this avoids: a tab
  // that decided at mount would hold the card step after another tab stored a full
  // token, or enter the shell with a token every customer route refuses.
  const storedToken = useSyncExternalStore(subscribeToTokenWrites, readStoredToken);
  // E23.9 FR9 — the token the backend last refused with `403 registration_incomplete`.
  // Stored as the token itself rather than a flag, so a new token (completion, another
  // tab, a fresh sign-in) is not refused merely by being stored after one that was.
  const [refusedToken, setRefusedToken] = useState("");
  // A render request for a token written in THIS tab when no other state changes with it,
  // which is the card step's completion: the session is already live, so nothing else
  // would re-render, and the full token would sit in storage behind a step still showing.
  const [, requestTokenRead] = useReducer((n) => n + 1, 0);
  const registrationSession =
    storedToken !== "" && (!isFullSessionToken(storedToken) || storedToken === refusedToken);
  // Whose session the stored token is. App keys the card step on it, so another account's token
  // remounts the step, and a result still in flight for the previous account is dropped with it
  // rather than committed over the new one.
  const sessionKey = getSessionKey(storedToken) || "";

  // ── OMEGA-1024 — platform maintenance (FR10, FR11, FR12) ──────────────────
  //
  // Two signals, one boolean. The POLL is the authority on what is true and on the
  // words shown; the EVENT is what makes the notice appear at the moment of refusal
  // rather than up to a minute later.
  //
  // The poll runs for everyone, signed in or not: a visitor on the login screen has
  // to see the notice too (US2), and it is the login and register screens that the
  // notice has to pre-empt. Offline mode has no backend, so it is disabled there.
  //
  // usePolledFetch keeps its last good payload when a fetch fails, which is the
  // behaviour this needs rather than an incidental one: a transient network error
  // must not reopen a Console an operator has closed. The mirror of the backend's
  // own FR9 grace period, on the client side of the same decision.
  const readPlatformStatus = useCallback(() => api.getPlatformStatus(), []);
  const { data: platformStatus, refresh: refreshPlatformStatus } = usePolledFetch(readPlatformStatus, {
    intervalMs: PLATFORM_STATUS_POLL_MS,
    enabled: USE_API,
  });

  // The mid-session signal, held separately from the polled payload because it knows
  // only that the platform refused, never the operator's message. Set by the event and
  // cleared ONLY by a poll that comes back available, so the thing that turns the
  // notice off is always a fresh reading rather than the absence of another refusal.
  const [maintenanceSignalled, setMaintenanceSignalled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    const evt = api.MAINTENANCE_EVENT || "omega:platform-maintenance";
    const onMaintenance = () => {
      setMaintenanceSignalled(true);
      // Ask for the operator's own copy straight away. Without this the notice would
      // show the default sentence for up to a minute even though a message exists.
      refreshPlatformStatus();
    };
    window.addEventListener(evt, onMaintenance);
    return () => window.removeEventListener(evt, onMaintenance);
  }, [refreshPlatformStatus]);

  // FR12 — maintenance cleared. No reload and nothing torn down: the session, the
  // stored token and every piece of state above are untouched, so the next render
  // lands back on whichever view was being shown before.
  useEffect(() => {
    if (platformStatus && platformStatus.active === false) setMaintenanceSignalled(false);
  }, [platformStatus]);

  // What App renders the notice from: an object while new workload is stopped, null
  // otherwise. `message` is empty when the operator wrote none, and the notice owns
  // the default copy for that case (FR14) rather than this hook inventing a sentence.
  const maintenance = platformStatus?.active
    ? { active: true, message: platformStatus.message || "", since: platformStatus.since || null }
    : maintenanceSignalled
      ? { active: true, message: "", since: null }
      : null;

  // Login is in flight. Exposed so LoginScreen can disable the Connect action
  // (visibility of status + duplicate-submission prevention). This UI guard is
  // defense in depth only — the loginGen guard below is the load-bearing
  // correctness fix and holds even if this flag is bypassed.
  const [authenticating, setAuthenticating] = useState(false);

  // Login-attempt generation — the login-SUCCESS analogue of the expiry-path
  // token-ownership guard. Out-of-order completion must NOT cross sessions: if
  // attempt B is started while A is still pending, A resolving LATE must not
  // overwrite B's token, resurrect A's identity, or re-schedule readiness.
  // Each doLogin() bumps this ref and captures its value; only the attempt whose
  // captured generation still equals loginGen.current at resolve time may commit
  // its token / flip loggedIn / schedule readiness. A ref (not state) so the
  // check is synchronous and always reflects the latest attempt.
  const loginGen = useRef(0);

  // The pending "Ready" delay before setLoggedIn(true). Held in a ref so logout,
  // a session-expiry, a fresh login attempt, or unmount can CANCEL it: without
  // this, an expiry during the 300ms window would race — logout sets loggedIn
  // false, then the stale timer flips it back true (authenticated with no
  // credential). scheduleReady() always cancels any prior timer first.
  const readyTimer = useRef(null);
  const cancelReadyTimer = useCallback(() => {
    if (readyTimer.current !== null) {
      clearTimeout(readyTimer.current);
      readyTimer.current = null;
    }
  }, []);
  const addLog = useCallback((m) => {
    setLoginLog((p) => [...p.slice(-16), `[${new Date().toISOString().split("T")[1].split(".")[0]}] ${m}`]);
  }, []);

  const scheduleReady = useCallback(() => {
    cancelReadyTimer();
    readyTimer.current = setTimeout(() => {
      readyTimer.current = null;
      // E23.9 — another tab signed out during the delay. The cross-tab listener below waits for
      // loggedIn, so nothing else noticed; entering now would open the shell with no credential.
      // The form stays filled, so signing in again is one click.
      if (USE_API && !api.hasToken()) {
        setAuthenticating(false);
        addLog("AUTH: Signed out in another tab");
        return;
      }
      setLoggedIn(true);
    }, READY_DELAY_MS);
  }, [cancelReadyTimer, addLog]);

  // Invalidate any pending ready-timer if the hook unmounts mid-delay.
  useEffect(() => cancelReadyTimer, [cancelReadyTimer]);

  const doLogin = useCallback(async () => {
    setLoginError("");
    setNeedsVerification(false);
    // A "your password was changed" notice describes the PREVIOUS action; once
    // the user acts on it by signing in, it stops being current.
    setLoginNotice("");

    if (!user || !password) {
      // OMEGA-814 — the login field is the customer's EMAIL. `user` keeps its name
      // because it is also the offline-auth display value and the session-key fallback;
      // what changed is what a customer types into it, not what it is for.
      setLoginError("Email and password are required");
      return;
    }

    // Defense in depth: ignore an overlapping submit while a login is in flight
    // so the login screen cannot fire duplicate attempts. The generation guard
    // below is what actually enforces correctness if this is ever bypassed.
    if (authenticating) return;

    // Claim a new generation for THIS attempt; the awaited outcome is committed
    // only if this generation is still the current one at resolve time.
    loginGen.current += 1;
    const gen = loginGen.current;
    setAuthenticating(true);
    // A fresh, PROCEEDING attempt supersedes any pending ready-delay from a prior
    // login. Cancel ONLY now that we've committed to this attempt: an invocation
    // that early-returned above (in flight, or blank fields) must NOT cancel the
    // in-flight winning attempt's ready timer — doing so before the guards would
    // deadlock login (loggedIn=false + authenticating=true, no timer to recover).
    cancelReadyTimer();

    addLog(`AUTH: Authenticating '${user}'...`);

    if (USE_API) {
      try {
        const resp = await api.login(user, password);
        // Latest-attempt-wins: a superseded (older) login resolving is a no-op —
        // do NOT persist its token, flip loggedIn, or schedule readiness.
        if (loginGen.current !== gen) return;
        // The winning attempt owns token persistence (api.login is thin
        // transport now); commit the JWT here so a stale attempt can't clobber it.
        api.setToken(resp.access_token);
        addLog("SEC: JWT token received");
        addLog("CONSOLE: Ready. Welcome " + resp.username);
        scheduleReady();
        return;
      } catch (err) {
        // A superseded attempt failing must not overwrite the winner's UI state.
        if (loginGen.current !== gen) return;
        setAuthenticating(false);
        addLog(`AUTH: FAIL ${err.message}`);
        // L01 FR9 — an unverified account is a 403 with a fixed error code;
        // surface a check-your-inbox message + resend affordance, distinct from a
        // 401. OMEGA-590 — key off the stable envelope `code` (readDetail now
        // returns the human `message`, not this machine code).
        if (err.code === "email_not_verified") {
          setNeedsVerification(true);
          setLoginError(
            "Your email is not verified yet. Check your inbox for the verification link, or resend it below.",
          );
        } else {
          setLoginError(err.message);
        }
        return;
      }
    }

    // Offline mode is opt-in only via VITE_OFFLINE_AUTH=true.
    if (loginGen.current !== gen) return;
    addLog("AUTH: Offline mode (no backend)");
    addLog("CONSOLE: Ready. Welcome " + user);
    scheduleReady();
  }, [user, password, authenticating, addLog, cancelReadyTimer, scheduleReady]);

  // L01 — resend the verification link for the identifier currently typed into the
  // login form (OMEGA-814: an email). Always succeeds server-side (202, no
  // enumeration); we surface a single neutral confirmation regardless. That silence is
  // why the key this is sent under has to match what the backend resolves on — a
  // mismatch here would look identical to success.
  const resendVerification = useCallback(async () => {
    if (!user || resendingVerification || resendOnCooldown) return;
    setResendingVerification(true);
    try {
      await api.resendVerification(user);
      setLoginError("If that account awaits verification, a fresh link is on its way. Check your inbox.");
      startResendCooldown();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setResendingVerification(false);
    }
  }, [user, resendingVerification, resendOnCooldown, startResendCooldown]);

  const logout = useCallback(() => {
    // Kill any pending ready-delay first so it cannot flip loggedIn back true
    // after we reset the session (the expiry handler routes through here too).
    cancelReadyTimer();
    // Supersede any login still in flight so its late resolution is a no-op
    // (it must not persist a token or resurrect the session after logout).
    loginGen.current += 1;
    setAuthenticating(false);
    api.clearToken();
    setLoggedIn(false);
    setUser("");
    setDisplayName("");
    setPassword("");
    setLoginLog([]);
    setLoginError("");
    setNeedsVerification(false);
    // No navigation here, deliberately. logout() is called from the shell's LOGOUT
    // button, from the session-expiry handler and from completeReset, and only the
    // first two want to move: App's shell guard sees `loggedIn` go false and redirects
    // to /login on its own. completeReset needs to stay exactly where it is, which a
    // navigate() in here would have made impossible without a flag parameter.
    // Session-scoped client state dies with the session. The onboarding
    // checklist's visited-screens record is per-customer, so leaving it behind
    // would tick a step for whoever logs in next on this tab. The expiry handler
    // below routes through here, so this covers both exits.
    endSession();
  }, [cancelReadyTimer]);

  // Bind the visited-screens record to the authenticated customer. One effect
  // rather than a call at each of the three login sites, and it clears only when
  // the OWNER changes, so it is a no-op on re-render within a session. This is
  // the backstop for the clear in logout(): even if a future auth path returned
  // to the console without passing through logout, the record could not carry
  // into a different customer's session.
  //
  // The owner comes from the CREDENTIAL, not from `user`. `loggedIn` is seeded
  // from api.hasToken() and so survives a reload; `user` is written only by the
  // login form and so does not. Keying off `user` left an ordinary authenticated
  // refresh (loggedIn=true, user="") with no owner at all, and markVisited drops
  // every visit while the record is unowned, so the checklist's Billing step
  // could never tick again after a refresh. `user` remains the fallback for
  // offline-auth mode, which has no token to read an identity from.
  //
  // Only the OWNERSHIP is re-established here. The visits themselves are still
  // lost on reload, by construction rather than by a call anyone could forget:
  // visitedScreens' Set is page-lifetime memory and a reload re-evaluates the
  // module. Losing them understates what the customer did, which is the right
  // direction to fail (see that module's header).
  //
  // E23.9 — keyed on `sessionKey` too, so another account's token stored over this
  // live session (another tab signing in as someone else) re-owns, and so clears, it.
  useEffect(() => {
    if (!loggedIn) return;
    beginSession(sessionKey || user);
  }, [loggedIn, sessionKey, user]);

  // E23.9 — one signed-in account replaced by another in place, which the card step's key
  // already expects. Who this tab says is signed in belongs to the previous account: drop it,
  // and the effect below reads the new one's name. Only between two signed-in accounts, so a
  // tab on the login screen keeps what is being typed into it.
  const previousSessionKey = useRef(sessionKey);
  useEffect(() => {
    const previous = previousSessionKey.current;
    previousSessionKey.current = sessionKey;
    if (!loggedIn || !previous || !sessionKey || previous === sessionKey) return;
    setUser("");
    setDisplayName("");
  }, [loggedIn, sessionKey]);

  // OMEGA-814 — resolve the display name from the profile whenever a session becomes
  // live. Keyed on `loggedIn` for exactly the reason the effect above is: `loggedIn` is
  // seeded from api.hasToken() and so survives a reload, while `user` is written only by
  // the login form and does not. Deriving the header from `user` would leave every
  // authenticated REFRESH with no identity at all, which is the bug this keying avoids.
  //
  // `full_name` is NULL for any account created before migration 091, so the fallback
  // chain ends at the username — the same honest degradation ProfilePanel makes. A failed
  // fetch is swallowed: the header falls back to the login identity, which is strictly
  // better than an error boundary over a decorative label.
  useEffect(() => {
    if (!loggedIn || !USE_API) return undefined;
    let cancelled = false;
    api
      .getProfile()
      .then((profile) => {
        if (!cancelled) setDisplayName(profile?.full_name || profile?.username || "");
      })
      .catch(() => {
        if (!cancelled) setDisplayName("");
      });
    // Guards against a logout (or a second sign-in) landing mid-flight and writing the
    // previous customer's name into the new session. Keyed on `sessionKey` as well (E23.9)
    // for an account replaced in place, which no change to `loggedIn` marks.
    return () => {
      cancelled = true;
    };
  }, [loggedIn, sessionKey]);

  // OMEGA-514 — session-expiry auto-logout. api.js fires SESSION_EXPIRED_EVENT
  // only when an ALREADY-authenticated call's credential is rejected (an
  // expired/invalid JWT or a revoked API key), NOT on a login or change-password
  // 401. The token is already cleared by api.js; logout() resets the rest of the
  // session state; App's shell guard then redirects to /login on the next render.
  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    const evt = api.SESSION_EXPIRED_EVENT || "omega:session-expired";
    const onExpired = () => logout();
    window.addEventListener(evt, onExpired);
    return () => window.removeEventListener(evt, onExpired);
  }, [logout]);

  // F14 FR11/FR12 — an authenticated password change revoked every JWT for this
  // account, the one in this browser included. api.js has already dropped the
  // token and fired PASSWORD_CHANGED_EVENT; reset the rest of the session state
  // and explain WHY the user is back at the login screen. Deliberately a second
  // listener rather than a branch inside the expiry one: the two events mean
  // different things to the user, and only this one can promise the new
  // password works.
  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    const evt = api.PASSWORD_CHANGED_EVENT || "omega:password-changed";
    const onChanged = () => {
      logout();
      setLoginNotice("Your password was changed and every signed-in session was ended. Sign in with your new password.");
    };
    window.addEventListener(evt, onChanged);
    return () => window.removeEventListener(evt, onChanged);
  }, [logout]);

  // E23.9 FR9 — the backend refused the current token as a registration session. It is
  // NOT a sign-out: the token stays (api.js leaves it alone) and is marked refused, so
  // registrationSession holds even if this tab's reading of the claim disagreed with the
  // backend's. api.js fires this only for a 403 issued under the token still stored, so
  // the token read here is the one refused.
  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    const evt = api.REGISTRATION_INCOMPLETE_EVENT || "omega:registration-incomplete";
    const onIncomplete = () => setRefusedToken(readStoredToken());
    window.addEventListener(evt, onIncomplete);
    return () => window.removeEventListener(evt, onIncomplete);
  }, []);

  // Another tab signed out. The token is shared by every tab, so this tab's session ended with it;
  // without this the card step would fall through to a shell holding no credential, which only that
  // shell's first 401 would end. Listening only while signed in, and only to the browser's
  // cross-tab event, so no write in this tab can reach it.
  useEffect(() => {
    if (!loggedIn || !USE_API || typeof window === "undefined" || !window.addEventListener) return undefined;
    const onStorage = () => {
      if (readStoredToken() === "") logout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [loggedIn, logout]);

  // E23.9 FR7, FR8 — the card step completed and the backend minted a full session. Like
  // login, the transport hands the token back rather than storing it, so it is committed
  // here; the step calls this only while it is still mounted, so a completion resolving
  // after a sign-out cannot bring the session back. The next render reads the full token
  // and App leaves the step for the shell.
  const handleRegistrationComplete = useCallback((resp) => {
    api.setToken(resp.access_token);
    requestTokenRead();
  }, []);

  // E23.9 FR8 — a card is on file, but this tab has no setup session to complete with
  // (S3 hands a ready tenant none, by design), so it cannot be exchanged for a full
  // session here. Sign out and say so: the next sign-in mints a full session.
  const handleCardOnFile = useCallback(() => {
    logout();
    setLoginNotice("A card is on file for your account. Sign in again to enter the Console.");
  }, [logout]);

  // F14 — drop the /reset-password landing and restore a clean URL. Doing this
  // is what removes the raw reset token from the address bar and from the
  // history entry (FR10), so it is not merely cosmetic routing.
  const clearReset = useCallback(() => {
    navigate(ROUTES.login, { replace: true });
  }, [navigate]);

  // F14 FR10 — the reset succeeded. Scrub the token-bearing URL and tear down any
  // session in this browser IMMEDIATELY (it was minted before the credential
  // version moved, so it is already dead server-side), but leave `resetPath`
  // standing so the success message the user is reading does not vanish
  // underneath them. They leave via leaveReset when they choose to.
  const completeReset = useCallback(() => {
    logout();
    // STAYS ON /reset-password, dropping only the `?token`. This is the one place a
    // router changes what the old code meant rather than just how it said it. The
    // routerless version called replaceState(..., "/") while leaving `resetPath` true,
    // so the address bar said "/" while the success message kept rendering: URL and
    // view were decoupled, and the comment above explains that was on purpose.
    //
    // Under a router the URL IS the view, so navigating to "/" here would unmount the
    // message the customer is reading. Navigating to the SAME path without the query
    // keeps the screen and still satisfies FR10, whose actual requirement is that the
    // raw token leaves the address bar and the history entry — `replace` is what does
    // that, not the destination.
    navigate(ROUTES.resetPassword, { replace: true });
    setLoginNotice("Your password has been reset. Sign in with your new password.");
  }, [logout, navigate]);

  // F14 FR8/FR14 — leave the reset screen for the login screen.
  const leaveReset = useCallback(() => {
    clearReset();
  }, [clearReset]);

  /**
   * A13 — leave the closure landing: drop the view and restore a clean URL.
   *
   * The token is scrubbed from the address bar here rather than on arrival, so the component
   * keeps the value it needs for the one POST while the link stops being shareable the moment
   * the customer is done with it. `closureToken` has no setter for the same reason the view
   * is torn down whole: there is no flow that swaps one token for another mid-screen.
   */
  const leaveClosure = useCallback(() => {
    navigate(ROUTES.login, { replace: true });
  }, [navigate]);

  // F14 FR14 — the link was invalid, expired or already used, so send the user
  // to the recovery form to request a fresh one rather than to a login screen
  // that cannot help them.
  const requestNewResetLink = useCallback(() => {
    setLoginError("");
    setLoginNotice("");
    setNeedsVerification(false);
    navigate(ROUTES.forgotPassword, { replace: true });
  }, [navigate]);

  // L01 — leave the /verify-email landing.
  // Leaves for "/", which App resolves against the session rather than guessing: a
  // successful verify has just stored a registration token so it lands on the card step,
  // and a failed one is still signed out so it lands on /login. One destination covers
  // both callers, which is why neither needs to know which case it is in.
  const clearVerify = useCallback(() => {
    navigate(ROUTES.root, { replace: true });
  }, [navigate]);

  // L01 FR8 — verify-email succeeded and stored the JWT; reuse the same
  // loggedIn path as login (auto-login on verify). Since E23.6 that JWT is a
  // registration session until a card is on file, so App.jsx shows the card step
  // rather than the Console (E23.9 FR1); this handler only signs the customer in.
  const handleVerified = useCallback((resp) => {
    setUser(resp?.username || "");
    setLoggedIn(true);
    clearVerify();
  }, [clearVerify]);

  // L01 — verify failed (invalid/expired/used); return to the login screen.
  const leaveVerify = useCallback(() => {
    clearVerify();
  }, [clearVerify]);

  // F02/L01: on the flag-off (dev) path api.register stores a JWT and returns a
  // TokenResponse; reuse the login path to sign the new user in. That JWT is a
  // registration session (E23.6), so App.jsx shows the card step, not the Console.
  // Under the deployed default (verification required) RegisterScreen shows the
  // check-your-inbox state instead and never calls this.
  const handleRegistered = useCallback((resp) => {
    setUser(resp?.username || "");
    setLoggedIn(true);
  }, []);

  // The history state the four auth-screen moves below must carry, and the reason they
  // take an argument at all. ADR 0009 made a signed-out deep link resumable by stashing
  // `{ from }` in history state when the shell guard bounces to /login. Every hop between
  // the auth screens is a `navigate`, and `navigate` writes NEW state, so a hop that passes
  // none ERASES it: login -> forgot password -> back to login used to drop the pending
  // destination silently and land the customer on the default screen after signing in.
  // Forwarding the whole object rather than picking `from` out of it is deliberate: these
  // moves are couriers, and a courier that understands its parcel has to be edited every
  // time the parcel changes shape.
  // A REF, not the value. `location.state` gets a new identity on every navigation, so
  // depending on it directly gave all four callbacks below new identities every time the
  // customer moved, and anything downstream holding them as props churned with it. The
  // callbacks only ever read this at call time, so a ref is both stable and current.
  // Measured: the value form turned e2e console-jobs:40 red while all 1311 unit tests
  // stayed green, because nothing in jsdom re-navigates often enough to show it.
  const authStateRef = useRef(location.state);
  authStateRef.current = location.state;

  // Switch the unauthenticated branch to the registration screen, dropping
  // any stale login error / verification notice so it does not leak onto the new form.
  const openRegister = useCallback(() => {
    setLoginError("");
    setNeedsVerification(false);
    setLoginNotice("");
    navigate(ROUTES.register, { state: authStateRef.current });
  }, [navigate]);

  // F14 FR2 — open the recovery view from the login screen. Same state hygiene as
  // openRegister: a login failure or verification notice belongs to the form the
  // user just left, and carrying it onto the recovery form would read as a
  // failure of the recovery request itself.
  const openForgot = useCallback(() => {
    setLoginError("");
    setNeedsVerification(false);
    setLoginNotice("");
    navigate(ROUTES.forgotPassword, { state: authStateRef.current });
  }, [navigate]);

  // F14 FR2 — the recovery view's route back to login.
  const closeForgot = useCallback(() => {
    setLoginError("");
    setLoginNotice("");
    navigate(ROUTES.login, { state: authStateRef.current });
  }, [navigate]);

  // The registration screen's way back. Its own callback rather than a second caller of
  // closeForgot: the two screens clear different state (recovery has no verification
  // notice to drop), and sharing one would have tied their hygiene together for no
  // reason beyond both ending on /login.
  const backToLogin = useCallback(() => {
    setLoginError("");
    setNeedsVerification(false);
    navigate(ROUTES.login, { state: authStateRef.current });
  }, [navigate]);

  return {
    loggedIn,
    registrationSession,
    sessionKey,
    maintenance,
    authenticating,
    user,
    setUser,
    displayName,
    password,
    setPassword,
    loginLog,
    loginError,
    loginNotice,
    needsVerification,
    leaveClosure,
    doLogin,
    logout,
    handleRegistered,
    handleVerified,
    handleRegistrationComplete,
    handleCardOnFile,
    leaveVerify,
    resendVerification,
    resendingVerification,
    resendCooldownRemaining,
    resendOnCooldown,
    openRegister,
    openForgot,
    closeForgot,
    backToLogin,
    completeReset,
    leaveReset,
    requestNewResetLink,
  };
}
